"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SCENARIOS = [
  {
    id: "support",
    label: "Customer Support",
    icon: "💬",
    outcome: "Issue resolved without human escalation",
    outcomeColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    transcript: [
      { role: "user", text: "My order hasn't arrived and it's been 10 days." },
      { role: "bot", text: "I'm sorry to hear that. Let me look up your order right away. Could you share your order number or the email address used at checkout?" },
      { role: "user", text: "It's #ORD-28471." },
      { role: "bot", text: "Found it. Your order shipped on the 2nd and was last scanned in the carrier's regional facility on the 5th. That's unusually delayed — I'm raising a trace request with the carrier now and flagging this for a replacement shipment if it isn't delivered by tomorrow. You'll get an email confirmation within 10 minutes. Is there anything else I can help with?" },
      { role: "user", text: "No, that's great. Thank you!" },
      { role: "bot", text: "You're welcome. If there's no update by tomorrow morning, check your email — the replacement process will already be underway." },
    ],
  },
  {
    id: "sales",
    label: "Sales & Qualification",
    icon: "📈",
    outcome: "Lead qualified and demo booked",
    outcomeColor: "text-violet-400 bg-violet-500/10 border-violet-500/20",
    transcript: [
      { role: "user", text: "Hi, I'm looking into AI tools for our HR department." },
      { role: "bot", text: "Great timing — we work with a number of HR teams. Are you looking to automate a specific workflow, or exploring more broadly where AI could help?" },
      { role: "user", text: "Mainly the CV screening process. We get 200+ applications per role." },
      { role: "bot", text: "That's a strong use case. Most clients in your position see screening time drop by 70–80% with an AI-assisted pipeline. How are you handling screening today — ATS-based filters, manual review, or a mix?" },
      { role: "user", text: "Mostly manual. Our ATS is Workday." },
      { role: "bot", text: "Good to know — we integrate directly with Workday. Would a 30-minute call with one of our HR AI specialists work this week? I can show you a live demo with a sample job spec and CV pool." },
      { role: "user", text: "Thursday afternoon works." },
      { role: "bot", text: "Thursday at 2 PM is confirmed. You'll get a calendar invite and a short pre-call questionnaire so we can tailor the demo to your team's workflow. Looking forward to it!" },
    ],
  },
  {
    id: "internal",
    label: "Internal Knowledge",
    icon: "🏢",
    outcome: "Policy answer retrieved in 4 seconds",
    outcomeColor: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    transcript: [
      { role: "user", text: "What's our policy on reimbursing home office equipment?" },
      { role: "bot", text: "According to the Remote Work Policy (last updated March 2025): employees working remotely full-time are eligible for a one-time home office allowance of up to £500, and up to £150 per year for consumables. Claims must be submitted within 90 days of purchase via Workday Expenses." },
      { role: "user", text: "Does that cover a standing desk?" },
      { role: "bot", text: "Yes — ergonomic furniture including standing desks and chairs is explicitly listed as eligible. You'll need to attach a receipt and select 'Home Office Equipment' as the category. Anything over £200 requires manager approval before purchase, per the policy." },
      { role: "user", text: "Perfect, thanks." },
      { role: "bot", text: "Happy to help. The Workday Expenses guide is linked in the IT handbook if you need it for future reference." },
    ],
  },
];

function BotIcon() {
  return (
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white text-[9px] font-bold shadow-md shadow-violet-500/30 flex-shrink-0">
      AI
    </div>
  );
}

function UserIcon() {
  return (
    <div className="w-7 h-7 rounded-full bg-zinc-700 border border-white/10 flex items-center justify-center flex-shrink-0">
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 text-zinc-400">
        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
      </svg>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3 items-start">
      <BotIcon />
      <div className="bg-violet-500/15 border border-violet-500/20 rounded-2xl rounded-tl-none px-4 py-3">
        <div className="flex gap-1 items-center">
          {[0, 1, 2].map((i) => (
            <motion.div key={i} className="w-1.5 h-1.5 rounded-full bg-violet-400"
              animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ message, visible }) {
  if (!visible) return null;
  const isBot = message.role === "bot";
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`flex gap-3 items-start ${!isBot ? "flex-row-reverse" : ""}`}>
      {isBot ? <BotIcon /> : <UserIcon />}
      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        isBot
          ? "bg-violet-500/15 border border-violet-500/20 text-zinc-200 rounded-tl-none"
          : "bg-zinc-800 border border-white/5 text-zinc-300 rounded-tr-none"
      }`}>
        {message.text}
      </div>
    </motion.div>
  );
}

export default function ChatbotDemo() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const intervalRef = useRef(null);
  const transcriptRef = useRef(null);
  const sectionRef = useRef(null);
  const scenario = SCENARIOS[activeScenario];

  function runPlayback(startFrom = 0, scenarioIdx = activeScenario) {
    clearInterval(intervalRef.current);
    setIsPlaying(true);
    setShowTyping(false);
    setVisibleCount(startFrom);
    let count = startFrom;
    const transcript = SCENARIOS[scenarioIdx].transcript;
    function showNext() {
      if (count >= transcript.length) { setShowTyping(false); setIsPlaying(false); return; }
      const msg = transcript[count];
      if (msg.role === "bot") {
        setShowTyping(true);
        setTimeout(() => {
          setShowTyping(false);
          count++;
          setVisibleCount(count);
          intervalRef.current = setTimeout(showNext, 900);
        }, 900);
      } else {
        count++;
        setVisibleCount(count);
        intervalRef.current = setTimeout(showNext, 1100);
      }
    }
    intervalRef.current = setTimeout(showNext, 400);
  }

  function handleScenarioChange(idx) {
    clearInterval(intervalRef.current);
    setActiveScenario(idx);
    setIsPlaying(false);
    setShowTyping(false);
    setVisibleCount(0);
    setTimeout(() => runPlayback(0, idx), 300);
  }

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAutoPlayed) {
        setHasAutoPlayed(true);
        setTimeout(() => runPlayback(0, 0), 600);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAutoPlayed]);

  useEffect(() => {
    if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [visibleCount, showTyping]);

  return (
    <section ref={sectionRef} className="bg-zinc-950 py-24 border-t border-white/[0.04]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-sm font-medium mb-4">
            <motion.span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block"
              animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
            Live demo
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">See it handle a real conversation</h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Pick a use case and watch how the bot qualifies, resolves, and converts — trained on your knowledge base.
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {SCENARIOS.map((s, i) => (
            <button key={s.id} onClick={() => handleScenarioChange(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeScenario === i
                  ? "bg-violet-600 text-white shadow-lg shadow-violet-500/25"
                  : "bg-zinc-800/80 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 border border-white/5"
              }`}>
              <span>{s.icon}</span>{s.label}
            </button>
          ))}
        </div>

        <div className="bg-zinc-900 rounded-2xl border border-white/[0.08] overflow-hidden shadow-2xl shadow-black/40">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06] bg-zinc-900/90">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/70" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <div className="w-3 h-3 rounded-full bg-green-500/70" />
              </div>
              <span className="text-sm text-zinc-400 font-medium">
                Asynq Chatbot<span className="text-zinc-600 mx-1.5">·</span>
                <span className="text-zinc-500">{scenario.label}</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <motion.span className="w-2 h-2 rounded-full bg-green-400"
                animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
              <span className="text-xs text-green-400 font-medium">Online</span>
            </div>
          </div>

          <div ref={transcriptRef} className="p-6 space-y-4 min-h-[340px] max-h-[420px] overflow-y-auto scroll-smooth">
            {scenario.transcript.map((message, i) => (
              <ChatMessage key={`${activeScenario}-${i}`} message={message} visible={i < visibleCount} />
            ))}
            {showTyping && <TypingIndicator />}
          </div>

          <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${scenario.outcomeColor}`}>
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                  <path fillRule="evenodd" d="M10.22 3.22a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 0 1-1.06 0L1.78 7.4a.75.75 0 0 1 1.06-1.06l1.6 1.6 4.72-4.72a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
                </svg>
                {scenario.outcome}
              </span>
              <span className="text-xs text-zinc-600">· Response latency &lt; 500ms</span>
            </div>
            <button onClick={() => runPlayback(0)} disabled={isPlaying}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold transition-all shrink-0">
              {isPlaying ? (
                <><motion.span className="w-1.5 h-1.5 rounded-full bg-white"
                  animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 0.8, repeat: Infinity }} />Playing…</>
              ) : (
                <><svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor"><path d="M2 2l8 4-8 4V2z" /></svg>Replay</>
              )}
            </button>
          </div>
        </div>

        <p className="text-center text-zinc-600 text-sm mt-6">
          Every conversation is trained on your knowledge base, tone, and workflows.
        </p>
      </div>
    </section>
  );
}
